/**
 * Comp Grants Admin Panel — Card 19 task B1
 *
 * Grant a user a free Pro or Grow upgrade with no card and no Stripe subscription, shorten or
 * extend an existing comp, revoke one, and comp a whole cohort at once for the live cutover.
 *
 * The server is the authority on every rule this UI expresses. In particular the shortening
 * floor — you cannot take back whole months a user has already had — is enforced in
 * plan-grant.service.ts and returned as `minimumExpiresAt` on each grant. This component uses
 * that value as the date input's `min` so the constraint is visible before you submit, and
 * surfaces the server's 409 when it is violated anyway. It never computes the floor itself:
 * two implementations of the same rule is how they diverge.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, describeApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { useToast } from '@/contexts/ToastContext';
import { Gift, RefreshCw, Ban, CalendarClock, X } from 'lucide-react';
import { UserSearchCombobox, displayName, type AdminUserResult } from './UserSearchCombobox';

interface PlanGrant {
  id: string;
  userId: string;
  grantedPlan: 'pro' | 'grow';
  grantedBy: string | null;
  grantedReason: string;
  startsAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  monthsConsumed: number;
  minimumExpiresAt: string;
}

const DATE_ONLY = (iso: string | null) => (iso ? iso.slice(0, 10) : '');

function formatDate(iso: string | null): string {
  if (!iso) return 'no end date';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC'
  });
}

function grantStatus(grant: PlanGrant): { label: string; tone: 'active' | 'ended' | 'revoked' } {
  if (grant.revokedAt) return { label: `Revoked ${formatDate(grant.revokedAt)}`, tone: 'revoked' };
  if (grant.expiresAt && new Date(grant.expiresAt).getTime() <= Date.now()) {
    return { label: `Ended ${formatDate(grant.expiresAt)}`, tone: 'ended' };
  }
  return {
    label: grant.expiresAt ? `Active until ${formatDate(grant.expiresAt)}` : 'Active, no end date',
    tone: 'active'
  };
}

export function PlanGrantsPanel() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  // Users are chosen from a search, never typed as a UUID — nobody knows anyone's UUID, and an
  // admin who mistypes one comps the wrong account silently.
  const [selectedUser, setSelectedUser] = useState<AdminUserResult | null>(null);
  const [lookupUser, setLookupUser] = useState<AdminUserResult | null>(null);
  const [cohort, setCohort] = useState<AdminUserResult[]>([]);
  const [grantedPlan, setGrantedPlan] = useState<'pro' | 'grow'>('pro');
  const [durationMonths, setDurationMonths] = useState('3');
  const [grantedReason, setGrantedReason] = useState('');

  const [editing, setEditing] = useState<Record<string, string>>({});

  const { data: grants, isLoading, refetch } = useQuery({
    queryKey: ['plan-grants', lookupUser?.id],
    queryFn: () => api.get<{ success: boolean; data: PlanGrant[] }>(`/api/workflows/plan-grants/user/${lookupUser!.id}`),
    enabled: Boolean(lookupUser?.id)
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['plan-grants'] });

  const createGrant = useMutation({
    mutationFn: () =>
      api.post('/api/workflows/plan-grants', {
        userId: selectedUser!.id,
        grantedPlan,
        grantedReason: grantedReason.trim(),
        // An empty duration means indefinite. That is a real choice, so it is made by clearing
        // the field rather than by a default that someone might not notice.
        ...(durationMonths ? { durationMonths: Number(durationMonths) } : {})
      }),
    onSuccess: () => {
      showSuccess('Comp granted', `${grantedPlan === 'grow' ? 'Grow' : 'Pro'} granted to ${displayName(selectedUser!)}`);
      // Jump the history panel to the account just comped, so the result is visible immediately.
      setLookupUser(selectedUser);
      setGrantedReason('');
      invalidate();
    },
    onError: (error: unknown) => showError('Could not grant comp', describeApiError(error))
  });

  const bulkGrant = useMutation({
    mutationFn: () =>
      api.post<{ data: { createdCount: number; failedCount: number } }>('/api/workflows/plan-grants/bulk', {
        userIds: cohort.map((u) => u.id),
        grantedPlan,
        grantedReason: grantedReason.trim(),
        ...(durationMonths ? { durationMonths: Number(durationMonths) } : {})
      }),
    onSuccess: (result: any) => {
      const { createdCount, failedCount } = result.data;
      // Partial success is normal here and is reported rather than hidden: one bad id should not
      // look like the whole cohort failed.
      if (failedCount > 0) {
        showError(`${createdCount} granted, ${failedCount} failed`, 'Re-running for just the failed ids is safe.');
      } else {
        showSuccess('Cohort comped', `${createdCount} grant(s) issued`);
      }
      setCohort([]);
      invalidate();
    },
    onError: (error: unknown) => showError('Bulk grant failed', describeApiError(error))
  });

  const editGrant = useMutation({
    mutationFn: ({ grantId, expiresAt }: { grantId: string; expiresAt: string }) =>
      api.patch(`/api/workflows/plan-grants/${grantId}`, {
        expiresAt: expiresAt ? new Date(`${expiresAt}T00:00:00Z`).toISOString() : null
      }),
    onSuccess: () => {
      showSuccess('Comp updated');
      invalidate();
    },
    onError: (error: unknown) =>
      // The server returns 409 with the floor in the message when you try to shorten below the
      // months already consumed. Show it verbatim — it names the date.
      showError('Could not update comp', describeApiError(error))
  });

  const revokeGrant = useMutation({
    mutationFn: (grantId: string) => api.post(`/api/workflows/plan-grants/${grantId}/revoke`),
    onSuccess: () => {
      showSuccess('Comp revoked', 'The user falls back to their paid plan on their next request.');
      invalidate();
    },
    onError: (error: unknown) => showError('Could not revoke comp', describeApiError(error))
  });

  const canSubmit = Boolean(selectedUser) && grantedReason.trim().length >= 3;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Gift className="w-4 h-4" />
            Grant a free upgrade
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            A comp creates no Stripe subscription and needs no card. When it expires the user
            moves back to Starter automatically on their next request — there is nothing to run
            and nothing to remember.
          </p>

          <UserSearchCombobox
            id="grant-user"
            label="User"
            selected={selectedUser}
            onSelect={setSelectedUser}
            placeholder="Search by name, email or company…"
          />

          <div className="grid gap-3 sm:grid-cols-2">

            <div className="space-y-1">
              <Label htmlFor="grant-plan">Plan</Label>
              <select
                id="grant-plan"
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={grantedPlan}
                onChange={(e) => setGrantedPlan(e.target.value as 'pro' | 'grow')}
              >
                <option value="pro">Pro ($29/mo value)</option>
                <option value="grow">Grow ($79/mo value)</option>
              </select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="grant-months">Months (blank = indefinite)</Label>
              <Input
                id="grant-months"
                type="number"
                min={1}
                max={120}
                value={durationMonths}
                onChange={(e) => setDurationMonths(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="grant-reason">Reason (required)</Label>
              <Input
                id="grant-reason"
                placeholder="e.g. Complimentary access"
                value={grantedReason}
                onChange={(e) => setGrantedReason(e.target.value)}
              />
            </div>
          </div>

          <p className="text-xs text-gray-500">
            The reason and your admin id are stored on the grant. They are what tell anyone
            reading a report six months from now that this account is free, and why.
          </p>

          <Button
            onClick={() => createGrant.mutate()}
            disabled={!canSubmit || createGrant.isPending}
          >
            {createGrant.isPending ? 'Granting…' : 'Grant comp'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Comp a cohort</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-gray-600">
            Add users one at a time; they collect below. Uses the plan, duration and reason set
            above. This is the live-cutover path — comping every approved user individually is a
            slog.
          </p>

          <UserSearchCombobox
            id="cohort-user"
            multi
            selected={null}
            onSelect={(user) => {
              if (!user) return;
              // Silently ignore a repeat pick rather than erroring: re-finding someone you already
              // added is a normal thing to do when the list gets long.
              setCohort((prev) => (prev.some((u) => u.id === user.id) ? prev : [...prev, user]));
            }}
            placeholder="Search a user to add to the cohort…"
          />

          {cohort.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {cohort.map((user) => (
                <span
                  key={user.id}
                  className="inline-flex items-center gap-1 rounded-full bg-gray-100 py-1 pl-3 pr-1 text-xs"
                >
                  <span className="max-w-[16rem] truncate">
                    {displayName(user)}
                    {user.trigUserEmail ? ` · ${user.trigUserEmail}` : ''}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${displayName(user)}`}
                    className="rounded-full p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
                    onClick={() => setCohort((prev) => prev.filter((u) => u.id !== user.id))}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <Button
            variant="outline"
            onClick={() => bulkGrant.mutate()}
            disabled={cohort.length === 0 || grantedReason.trim().length < 3 || bulkGrant.isPending}
          >
            {bulkGrant.isPending
              ? 'Granting…'
              : `Grant to ${cohort.length || 'all listed'} user${cohort.length === 1 ? '' : 's'}`}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarClock className="w-4 h-4" />
            Existing comps
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <UserSearchCombobox
                id="lookup-user"
                selected={lookupUser}
                onSelect={setLookupUser}
                placeholder="Search a user to see their comp history…"
              />
            </div>
            <Button variant="outline" size="icon" onClick={() => refetch()} disabled={!lookupUser}>
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {!lookupUser && (
            <p className="text-sm text-gray-500">Search for a user to see their comp history.</p>
          )}

          {lookupUser && !isLoading && (grants?.data?.length ?? 0) === 0 && (
            <p className="text-sm text-gray-500">No comps for {displayName(lookupUser)}.</p>
          )}

          {grants?.data?.map((grant) => {
            const status = grantStatus(grant);
            const draft = editing[grant.id] ?? DATE_ONLY(grant.expiresAt);
            return (
              <div key={grant.id} className="rounded-lg border p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={grant.grantedPlan === 'grow' ? 'default' : 'secondary'}>
                    {grant.grantedPlan === 'grow' ? 'Grow' : 'Pro'} — comped
                  </Badge>
                  <span
                    className={
                      status.tone === 'active'
                        ? 'text-sm text-green-700'
                        : 'text-sm text-gray-500'
                    }
                  >
                    {status.label}
                  </span>
                </div>

                <p className="text-sm text-gray-700">{grant.grantedReason}</p>
                <p className="text-xs text-gray-500">
                  Started {formatDate(grant.startsAt)} · {grant.monthsConsumed} whole month(s) used
                </p>

                {!grant.revokedAt && (
                  <div className="flex flex-wrap items-end gap-2 pt-1">
                    <div className="space-y-1">
                      <Label className="text-xs" htmlFor={`expires-${grant.id}`}>
                        End date
                      </Label>
                      <Input
                        id={`expires-${grant.id}`}
                        type="date"
                        // The earliest the server will accept. Shortening past this would be
                        // taking back time the user already had.
                        min={DATE_ONLY(grant.minimumExpiresAt)}
                        value={draft}
                        onChange={(e) => setEditing((prev) => ({ ...prev, [grant.id]: e.target.value }))}
                        className="w-44"
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={draft === DATE_ONLY(grant.expiresAt) || editGrant.isPending}
                      onClick={() => editGrant.mutate({ grantId: grant.id, expiresAt: draft })}
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600"
                      disabled={revokeGrant.isPending}
                      onClick={() => revokeGrant.mutate(grant.id)}
                    >
                      <Ban className="w-3.5 h-3.5 mr-1" />
                      Revoke
                    </Button>
                  </div>
                )}

                {!grant.revokedAt && (
                  <p className="text-xs text-gray-400">
                    Cannot end before {formatDate(grant.minimumExpiresAt)} — months already used.
                  </p>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}

export default PlanGrantsPanel;
